import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { QuoteStatusStats } from '../types';

interface OrderStatsBarProps {
  stats: QuoteStatusStats[];
  className?: string;
}

const statusConfig: Record<
  string,
  { labelKey: string; valueColor?: string }
> = {
  approved: {
    labelKey: 'stats.approved',
    valueColor: 'text-green-600',
  },
  inprogress: {
    labelKey: 'stats.inProgress',
    valueColor: 'text-blue-600',
  },
  'invoice-ready': {
    labelKey: 'stats.invoiceReady',
    valueColor: 'text-purple-600',
  },
  'partially-invoiced': {
    labelKey: 'stats.partiallyInvoiced',
    valueColor: 'text-orange-600',
  },
  invoiced: {
    labelKey: 'stats.invoiced',
    valueColor: 'text-gray-600',
  },
  total: {
    labelKey: 'stats.total',
    valueColor: 'text-gray-900',
  },
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function OrderStatsBar({ stats, className }: OrderStatsBarProps) {
  const { t } = useTranslation('orders');

  const orderedStatuses: string[] = [
    'approved',
    'inprogress',
    'invoice-ready',
    'partially-invoiced',
    'invoiced',
    'total',
  ];

  const orderedStats = orderedStatuses
    .map((status) => {
      const stat = stats.find((s) => s.status === status);
      if (!stat) return null;
      const config = statusConfig[status];
      if (!config) return null;
      return { ...stat, config };
    })
    .filter(Boolean);

  if (orderedStats.length === 0) {
    return null;
  }

  return (
    <div className={cn('grid gap-4 md:grid-cols-6', className)}>
      {orderedStats.map((stat) => {
        if (!stat) return null;
        return (
          <Card key={stat.status}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t(stat.config.labelKey)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className={cn('text-2xl font-bold', stat.config.valueColor)}>
                {stat.quantity.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(stat.amount)}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
